import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import Button from "../../../components/ui/Button/Button";
import { adminApi } from "../../../lib/admin";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatDateTime } from "../../../utils/DateUtils";

const AdminEmailModerationApprovalPage = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId")?.trim() ?? "";
  const token = searchParams.get("token")?.trim() ?? "";
  const hasValidParams = requestId.length > 0 && token.length > 0;

  const approveMutation = useMutation({
    mutationFn: () => adminApi.approveEmailModeration(requestId, { token }),
    onSuccess: async (approvedReview) => {
      queryClient.setQueryData(["admin-email-moderation-review", requestId, token], approvedReview);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
  });

  const reviewQuery = useQuery({
    queryKey: ["admin-email-moderation-review", requestId, token],
    queryFn: () => adminApi.getEmailModerationReview(requestId, token),
    enabled: hasValidParams && !approveMutation.isSuccess,
  });

  const review = approveMutation.data ?? reviewQuery.data;
  const errorMessage = !hasValidParams
    ? "This approval link is missing required information."
    : approveMutation.data
      ? null
      : reviewQuery.error
      ? apiErrorToMessage(reviewQuery.error, "Unable to load the moderation request.")
      : approveMutation.error
        ? apiErrorToMessage(approveMutation.error, "Unable to approve the moderation request.")
        : null;

  const isApproved = review?.status === "APPROVED";
  const eyebrow = review?.action === "UNBAN" ? "Email Unban Approval" : "Email Ban Approval";
  const title = isApproved
    ? `${review?.action === "UNBAN" ? "Unban" : "Ban"} approved`
    : `Review ${review?.action === "UNBAN" ? "email unban" : "email ban"} request`;

  return (
    <main className="mx-auto max-w-3xl px-6 py-10">
      <div className="rounded-3xl border border-myteengig-border bg-white shadow-sm">
        <div className="border-b border-myteengig-border px-8 py-8">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-myteengig-primary">{eyebrow}</p>
          <h1 className="mt-3 text-3xl font-bold text-myteengig-text-primary">{title}</h1>
          <p className="mt-4 max-w-2xl text-sm text-myteengig-text-muted">
            This approval applies the moderation action for the target email and writes the final action to the admin audit log.
          </p>
        </div>

        <div className="space-y-6 px-8 py-8">
          {reviewQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading moderation request...</p> : null}
          {errorMessage ? <p className="text-sm font-medium text-red-600">{errorMessage}</p> : null}

          {review ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Action</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{review.action}</p>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{review.status}</p>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Email state</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">
                    {review.currentlyBanned ? "Currently banned" : "Not banned"}
                  </p>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Requested</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{formatDateTime(review.requestedAt)}</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Target</p>
                  <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                    <p>{review.normalizedEmail}</p>
                    <p>{review.targetUserName ?? "No linked user"}</p>
                    <p>{review.targetUserEmail ?? "No linked account email"}</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Requested by</p>
                  <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                    <p>{review.requestedByAdminName}</p>
                    <p>{review.requestedByAdminEmail}</p>
                    {review.approvedAt ? <p>Approved: {formatDateTime(review.approvedAt)}</p> : null}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-myteengig-border p-4">
                <p className="text-sm font-semibold text-myteengig-text-primary">Reason</p>
                <p className="mt-3 text-sm text-myteengig-text-muted">{review.reason}</p>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <Link
                  to="/admin"
                  className="inline-flex items-center rounded-md border border-myteengig-border px-4 py-2 text-sm font-semibold text-myteengig-text-primary transition hover:bg-myteengig-background"
                >
                  Back to admin
                </Link>
                <Button
                  disabled={!hasValidParams || isApproved || approveMutation.isPending}
                  onClick={() => approveMutation.mutate()}
                >
                  {approveMutation.isPending
                    ? "Approving..."
                    : isApproved
                      ? `${review.action === "UNBAN" ? "Unban" : "Ban"} Approved`
                      : `Approve ${review.action === "UNBAN" ? "Unban" : "Ban"}`}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
};

export default AdminEmailModerationApprovalPage;
